import { COMPANY, whatsappUrl } from "@/lib/company";
import { LAUNCH_OFFER } from "@/lib/pricing";

/**
 * The Listing Creative Pack: the creative half of a Property Launch, sold on
 * its own as a first purchase.
 *
 * Kristian's idea of 5 Oct 2026. Five days of ads had brought two enquiries
 * that went quiet: a "free written review" is abstract to someone who has
 * never heard of Adkiro, while four ads for one listing, at a fixed price and
 * in three working days, is something to buy.
 *
 * It lives on its own page and is not in OFFERS, on purpose. On 1 Oct he
 * rejected a cheaper price list as the wrong signal for agencies and
 * developers, so the pack is the step before the Property Launch rather than
 * a sixth row in the main list, and its price is credited when a client
 * takes the launch for the same listing.
 */
export const PACK_NAME = "Listing Creative Pack";

/** Days the first purchase stays creditable against a Property Launch. */
export const PACK_CREDIT_DAYS = 30;

export interface PackPlan {
  id: "one" | "five" | "launch";
  name: string;
  price: string;
  schemaPrice: number;
  terms: string;
  features: string[];
  /** The plan the page leans on: filled, where the others are outlined. */
  featured?: boolean;
  /**
   * Stripe Payment Link, behind the pay button on the plan's card and also
   * sent by Kristian with the written confirmation. For a few hours on 5 Oct
   * 2026 it was not a button; he saw the live page and asked for one. It
   * redirects to /start?service=pack or pack5.
   */
  payUrl?: string;
}

/** What a pack's payment link calls it in /start?service=…, beside the ids in OFFERS. */
export const PACK_START_IDS = ["pack", "pack5"] as const;
export type PackStartId = (typeof PACK_START_IDS)[number];

export const PACK_ONE: PackPlan = {
  id: "one",
  name: "One listing",
  price: "$450",
  schemaPrice: 450,
  terms: "One time. To try it.",
  features: [
    "Four ad designs, in two sizes each",
    "Two short videos",
    "Three texts and three headlines",
    "Ready in three working days",
    "One round of changes",
  ],
  payUrl: "https://buy.stripe.com/00wcMZ8zP3bSbMsg2hg7e06",
};

export const PACK_FIVE: PackPlan = {
  id: "five",
  name: "Five listings",
  price: "$1,750",
  schemaPrice: 1750,
  terms: "$350 each. Use them within 60 days.",
  features: [
    "Everything in “One listing”, five times",
    "One style across all five",
    "One listing every two working days",
    "One contact, in writing",
  ],
  featured: true,
  payUrl: "https://buy.stripe.com/dRm6oBg2h7s8aIo03jg7e07",
};

/** The third way to buy is the existing Property Launch, so its price is read from there. */
export const PACK_WITH_CAMPAIGN: PackPlan = {
  id: "launch",
  name: "With the campaign",
  price: LAUNCH_OFFER.price,
  schemaPrice: LAUNCH_OFFER.schemaPrice ?? 2500,
  terms: `One time. Our ${LAUNCH_OFFER.name}.`,
  features: [
    "The pack for one listing",
    "Campaign plan and setup in your own ad account",
    "30 days of management and optimisation",
    "A written update every week",
  ],
};

export const PACK_PLANS = [PACK_ONE, PACK_FIVE, PACK_WITH_CAMPAIGN];

export const PACK_CREDIT_NOTE = `The ${PACK_ONE.price} is credited in full if you take the ${LAUNCH_OFFER.name} for the same listing within ${PACK_CREDIT_DAYS} days.`;

/** The first message, already written, so an enquiry about the pack is recognisable as one. */
export const PACK_WHATSAPP_URL = whatsappUrl(
  `Hello Kristian, I'd like the ${PACK_NAME}. Here is the listing: `,
);

export const PACK_EMAIL_URL = `mailto:${COMPANY.email}?subject=${encodeURIComponent(PACK_NAME)}&body=${encodeURIComponent(
  "Hello Kristian,\n\nI'd like the Listing Creative Pack. Here is the listing:\n\n",
)}`;
